import { useState } from "react";
import { Check, RotateCcw, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiError, euro } from "@/lib/api";
import { toast } from "sonner";

export const ManualPaymentAction = ({ booking, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState(["cash", "bank_transfer", "manual"].includes(booking.payment_method) ? booking.payment_method : "");
  const [paidAt, setPaidAt] = useState("");
  const [error, setError] = useState("");
  const repair = booking.payment_status === "paid" && booking.status === "expired";
  const id = booking.booking_number;
  if (["cancelled", "removed", "membership_rejected", "waitlist"].includes(booking.status) || booking.payment_status === "refunded" || (booking.payment_status === "paid" && !repair)) return null;
  const fieldCls = "mt-1 w-full bg-[#1A1A1A] border border-white/20 p-2.5 text-sm text-white focus:border-[#ff0033] outline-none";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/admin/bookings/${booking.id}/${repair ? "restore-participation" : "mark-paid"}`, {
        payment_method: method,
        ...(paidAt ? { paid_at: new Date(paidAt).toISOString() } : {}),
        note: repair ? "Bezahlte, abgelaufene Buchung nach Prüfung wiederhergestellt" : "Zahlung manuell bestätigt",
      });
      toast.success(repair ? "Teilnahme wiederhergestellt · Rechnung abgeglichen" : "Zahlung und Teilnahme bestätigt · Rechnung abgeglichen");
      setOpen(false); onSaved();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };
  return <>
    {repair && <p role="alert" data-testid={`paid-expired-warning-${id}`} className="text-xs text-amber-400 mt-1 mb-2">Bezahlt, aber abgelaufen – nicht in der Teilnehmerliste.</p>}
    <button type="button" onClick={() => { setError(""); setOpen(true); }} data-testid={`${repair ? "restore-participation" : "mark-paid"}-${id}`}
      className={`inline-flex items-center gap-1 text-left text-xs transition-colors ${repair ? "text-amber-400 hover:text-amber-200" : "text-green-400 hover:text-green-200"}`}>
      {repair ? <RotateCcw size={13} /> : <Check size={13} />}{repair ? "Teilnahme wiederherstellen" : "Als bezahlt"}
    </button>
    <Dialog open={open} onOpenChange={(value) => { if (!busy) setOpen(value); }}>
      <DialogContent data-testid={`manual-payment-dialog-${id}`} className="bg-[#0F0F0F] text-white border-white/20 w-[calc(100%-2rem)] max-w-lg">
        <DialogTitle data-testid={`manual-payment-title-${id}`}>{repair ? "Teilnahme wiederherstellen" : "Zahlung bestätigen"}</DialogTitle>
        <DialogDescription data-testid={`manual-payment-summary-${id}`} className="text-neutral-400 break-words">
          {booking.first_name} {booking.last_name} · {id} · {euro(booking.amount || 0)}
        </DialogDescription>
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm">Zahlungsart
            <select required value={method} onChange={(e) => setMethod(e.target.value)} data-testid={`manual-payment-method-${id}`} className={fieldCls}>
              <option value="" disabled>Bitte auswählen</option><option value="cash">Barzahlung</option>
              <option value="bank_transfer">Überweisung</option><option value="manual">Sonstige manuelle Zahlung</option>
            </select>
          </label>
          <label className="block text-sm">Tatsächlicher Zahlungseingang (optional)
            <input type="datetime-local" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid={`manual-payment-date-${id}`} className={fieldCls} />
          </label>
          {repair && <p data-testid={`manual-payment-repair-notice-${id}`} className="text-xs text-neutral-400">Die bestehende Rechnung wird abgeglichen. Es wird keine erneute Zahlung ausgelöst und keine E-Mail versendet.</p>}
          {error && <p role="alert" data-testid={`manual-payment-error-${id}`} className="text-sm text-red-400">{error}</p>}
          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" disabled={busy} onClick={() => setOpen(false)} data-testid={`manual-payment-cancel-${id}`} className="px-4 py-2 border border-white/20 hover:border-white/50 transition-colors disabled:opacity-50">Abbrechen</button>
            <button type="submit" disabled={busy || !method} data-testid={`manual-payment-submit-${id}`} className="px-4 py-2 bg-[#ff0033] hover:bg-[#cc0029] transition-colors disabled:opacity-50 inline-flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />}Bestätigen</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  </>;
};